import { useState } from "react";
import { api } from "@/api";
import { toast } from "@/lib/toast";
import { X, Copy, ArrowRight } from "lucide-react";
import { FISCAL_YEARS } from "@/constants/modules";

export default function DuplicateYearModal({ clientId, currentYear, jurisdiction, onClose, onDone }) {
  const [fromYear, setFromYear] = useState(FISCAL_YEARS.find(y => y !== currentYear) || currentYear - 1);
  const [toYear] = useState(currentYear);
  const [resetAmounts, setResetAmounts] = useState(true);
  const [applyJurisdictionFilter, setApplyJurisdictionFilter] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleDuplicate = async () => {
    if (!clientId) return;
    if (fromYear === toYear) { toast.error("Les annees doivent differer"); return; }
    setLoading(true);
    try {
      const body = {
        client_id: clientId,
        from_year: fromYear,
        to_year: toYear,
        reset_amounts: resetAmounts,
      };
      if (applyJurisdictionFilter) body.jurisdiction = jurisdiction;
      const r = await api.post("/tax-lines/duplicate", body);
      const { duplicated, skipped } = r.data || {};
      if (duplicated > 0) {
        toast.success(`${duplicated} ligne(s) dupliquee(s) vers ${toYear}${skipped ? `, ${skipped} ignoree(s)` : ""}`);
        onDone();
      } else {
        toast.info(`Aucune ligne a dupliquer (${skipped || 0} deja presentes)`);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur duplication");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="duplicate-year-modal">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-md overflow-hidden">
        <div className="px-5 py-3 border-b flex items-center justify-between bg-emerald-50">
          <h3 className="font-semibold flex items-center gap-2 text-emerald-800">
            <Copy className="w-4 h-4" /> Dupliquer les lignes d'une annee
          </h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-5 space-y-4">
          <div className="flex items-center gap-3">
            <div className="flex-1">
              <label className="block text-xs font-medium text-gray-600 mb-1">Depuis l'annee</label>
              <select value={fromYear} onChange={e => setFromYear(parseInt(e.target.value))}
                className="w-full border rounded-lg px-3 py-2 text-sm" data-testid="dup-from-year">
                {FISCAL_YEARS.filter(y => y !== currentYear).map(y => <option key={y} value={y}>{y}</option>)}
              </select>
            </div>
            <ArrowRight className="w-5 h-5 text-emerald-600 mt-5" />
            <div className="flex-1">
              <label className="block text-xs font-medium text-gray-600 mb-1">Vers (annee active)</label>
              <input type="text" value={toYear} readOnly
                className="w-full border rounded-lg px-3 py-2 text-sm bg-gray-50 font-semibold" />
            </div>
          </div>

          <label className="flex items-start gap-2 p-3 border rounded-lg hover:bg-gray-50 cursor-pointer">
            <input type="checkbox" checked={resetAmounts} onChange={e => setResetAmounts(e.target.checked)}
              className="mt-0.5" data-testid="dup-reset-amounts" />
            <div>
              <p className="text-sm font-medium">Remettre les montants a 0</p>
              <p className="text-xs text-gray-500">Recommande : seule la structure des lignes est reprise, les montants sont vides.</p>
            </div>
          </label>

          <label className="flex items-start gap-2 p-3 border rounded-lg hover:bg-gray-50 cursor-pointer">
            <input type="checkbox" checked={applyJurisdictionFilter} onChange={e => setApplyJurisdictionFilter(e.target.checked)}
              className="mt-0.5" data-testid="dup-jur-filter" />
            <div>
              <p className="text-sm font-medium">Seulement la juridiction active ({jurisdiction === "federal" ? "Federal" : "Provincial"})</p>
              <p className="text-xs text-gray-500">Si decoche : federal + provincial sont dupliques.</p>
            </div>
          </label>

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-xs text-blue-800">
            Les lignes deja presentes dans {toYear} ne seront pas ecrasees — la duplication ne cree que les lignes manquantes.
          </div>
        </div>

        <div className="px-5 py-3 border-t bg-gray-50 flex gap-2 justify-end">
          <button onClick={onClose} className="px-4 py-2 border rounded-lg text-sm hover:bg-gray-100">Annuler</button>
          <button onClick={handleDuplicate} disabled={loading}
            className="px-4 py-2 bg-emerald-600 text-white rounded-lg text-sm hover:bg-emerald-700 disabled:opacity-50 flex items-center gap-1"
            data-testid="dup-confirm">
            <Copy className="w-3.5 h-3.5" /> {loading ? "Duplication..." : "Dupliquer"}
          </button>
        </div>
      </div>
    </div>
  );
}
